/** @jsx jsx */
import { useContext, useState, useEffect, Fragment, useMemo } from 'react'
import { jsx, Card, Flex, Text } from 'theme-ui'
import { navigate } from '@reach/router'
import { QuestionStepContext } from '../../context/QuestionStepContext'
import { Formik, Form } from 'formik'
import { useMutation, useQuery } from '@apollo/client'
import SUBMIT_ANSWERS, {
  TEMPLATE_STATUS,
} from '../../apollo/queries/SUBMIT_ANSWERS'
import GET_QUESTIONS from '../../apollo/queries/GET_QUESTIONS'
import GET_PERSONAS from '../../apollo/queries/GET_PERSONAS'
import GET_TEMPLATE_OVERVIEW, {
  STRATEGYBUILDER_SECTION_NAME,
  TEMPLATE_CATEGORY,
} from '../../apollo/queries/GET_TEMPLATE_OVERVIEW'
import {
  cleanUpIsHide,
  parseSelectionAnswers,
} from '../common/form/FormikHelper'
import QuestionButtonRow from '../common/QuestionButtonRow'
import ProgressBar from '../common/ProgressBar'
import LoadingSpinner from '../common/LoadingSpinner'
import Matrix from '../common/Matrix'
import { groupBy, isEmpty } from 'lodash'
import QuestionType from './QuestionType'
import { any } from 'prop-types'
import { graphql, useStaticQuery } from 'gatsby'
import { getDeliverablesByCategory } from '../../utils/strategyBuilder'
import Persona from './Completed/Persona'
import { Link } from 'gatsby'

export const GET_PERSONA_AND_ADDITIONAL_SERVICES = graphql`
  {
    allContentfulStrategyBuilderDeliverableCardV2 {
      edges {
        node {
          id
          title
          category
          description {
            description
          }
          descriptionBrandPositioning {
            descriptionBrandPositioning
          }
          image {
            file {
              url
            }
          }
          url {
            file {
              url
            }
          }
        }
      }
    }
    allContentfulStrategyBuilderSummary(
      filter: { title: { eq: "Strategy Builder Target Mark Summary Buttons" } }
    ) {
      nodes {
        additionalServices {
          id
          text
          title
          url
        }
      }
    }
  }
`

const parsePercentGroupAnswers = (values, template) => {
  const { questionIsPercentGroupSection, questions, currentStep } = template
  const questionsAndAnswers = []
  if (questionIsPercentGroupSection) {
    const oldRetirementAnswer = questions[currentStep]?.[0]?.answers.find(
      answer => answer.id === '29'
    )
    const currentRetirementAnswerValue = values['29']
    if (
      oldRetirementAnswer &&
      oldRetirementAnswer.answerValue !== currentRetirementAnswerValue
    ) {
      const oldValue = oldRetirementAnswer.userSelectedAnswer
        ? parseInt(
            oldRetirementAnswer.answerValue.substring(
              0,
              oldRetirementAnswer.answerValue.length
            )
          )
        : 0
      const currentValue = currentRetirementAnswerValue
        ? parseInt(
            currentRetirementAnswerValue.substring(
              0,
              currentRetirementAnswerValue.length
            )
          )
        : 0
      if (
        (oldValue < 25 && currentValue >= 25) ||
        (oldValue >= 25 && currentValue < 25)
      ) {
        // if the user's new answer would change the following questions in the section, we need to reset those answer values
        questions.forEach((questionSection, sectionIndex) => {
          if (sectionIndex > 0) {
            questionSection.forEach(question => {
              questionsAndAnswers.push({
                questionId: question.id,
                answers: [],
              })
            })
          }
        })
      }
    }
  }
  const answerIds = Object.keys(values)
  if (answerIds.length) {
    const answers = Object.keys(values).map(answerId => {
      return { answerId, answerValue: values[answerId] }
    })
    const question = questions[currentStep]?.find(question =>
      question.answers?.some(answer => answer.id === answers[0]?.answerId)
    )
    if (question) {
      questionsAndAnswers.push({
        questionId: question.id,
        answers,
      })
    }
  }
  return questionsAndAnswers
}

export const parseAnswers = (values, template) => {
  const { questionIsPercentGroupSection, questions, currentStep } = template
  if (questionIsPercentGroupSection) {
    return parsePercentGroupAnswers(values, template)
  }
  const selectionAnswers = {}
  const writeInAnswers = []
  const textAnswers = []
  let inlinePercentAnswers = []
  Object.keys(values).forEach(uniqueId => {
    const question = questions[currentStep]?.find(
      question => question.id === uniqueId
    )
    if (
      question?.answers[0].answerType === 'textarea' ||
      question?.answers[0].answerType === 'text' ||
      question?.answers[0].answerType === 'number'
    ) {
      textAnswers.push({
        questionId: question.id,
        answers: [
          {
            answerId: question?.answers[0]?.id,
            answerValue: values[uniqueId].toString(),
          },
        ],
      })
    } else {
      if (question) {
        // if clearing a selection that previously had a value, we need to update that value to null
        selectionAnswers[uniqueId] = values[uniqueId]?.length
          ? values[uniqueId]
          : null
      } else {
        // percent inputs and write-in answers within selection groups are mapped to answer id
        // if we are here we have one of those
        const foundQuestionFromAnswer = questions[currentStep]?.find(question =>
          question.answers?.some(answer => answer.id === uniqueId)
        )
        // if it is a write in answer, the first answer type will be a radio or checkbox
        if (
          foundQuestionFromAnswer?.answers[0]?.answerType === 'radio' ||
          foundQuestionFromAnswer?.answers[0]?.answerType === 'checkbox'
        ) {
          const answerIsCheckbox =
            foundQuestionFromAnswer?.answers[0]?.answerType === 'checkbox'

          // write in textfields are always preceeded by their corresponding radio or checkbox
          const textFieldIndex = foundQuestionFromAnswer?.answers.findIndex(
            answer => answer.id === uniqueId
          )
          const writeInOption =
            foundQuestionFromAnswer?.answers[textFieldIndex - 1]
          const writeInOptionSelected = answerIsCheckbox
            ? values[foundQuestionFromAnswer.id].findIndex(
                answerId => answerId === writeInOption?.id
              ) > -1
            : writeInOption?.id === values[foundQuestionFromAnswer.id]

          // if the user selected the answer associated with a write in option, we need to store the text value.
          if (writeInOptionSelected) {
            writeInAnswers.push({
              questionId: foundQuestionFromAnswer.id,
              answer: {
                answerId: uniqueId,
                answerValue: values[uniqueId],
              },
            })
          }
        } else {
          if (!inlinePercentAnswers.length) {
            const percentValues = {}
            foundQuestionFromAnswer?.answers.forEach(answer => {
              if (values[answer.id]) {
                percentValues[answer.id] = values[answer.id]
              }
            })
            inlinePercentAnswers = parsePercentGroupAnswers(
              percentValues,
              template
            )
          }
        }
      }
    }
  })

  const parsedSelectionAnswers = parseSelectionAnswers(selectionAnswers)

  if (writeInAnswers.length) {
    writeInAnswers.forEach(writeIn => {
      const selectionAnswer = parsedSelectionAnswers.find(
        answer => answer.questionId === writeIn.questionId
      )
      if (selectionAnswer) {
        selectionAnswer.answers.push(writeIn.answer)
      }
    })
  }
  return [...parsedSelectionAnswers, ...textAnswers, ...inlinePercentAnswers]
}

const QuestionCard = ({ templatesOverview }) => {
  const { data: personaPlans } = useQuery(GET_PERSONAS)
  const {
    allContentfulStrategyBuilderDeliverableCardV2: contentfulData,
  } = useStaticQuery(GET_PERSONA_AND_ADDITIONAL_SERVICES)

  const {
    deliverables,
    setDeliverables,
    deliverablesByCategory,
    setDeliverablesByCategory,
    template,
    setSavingProgress,
    updateTemplate,
    userHitBack,
    answersUpdating,
    questionProgressStep,
    setQuestionProgressStep,
    totalQuestionProgressSteps,
    setTotalQuestionProgressSteps,
    generateFormikSchema,
    formikSchema,
    rawTemplate,
    percentDesiredError,
    percentCurrentError,
  } = useContext(QuestionStepContext)
  useEffect(() => {
    if (!isEmpty(contentfulData))
      setDeliverablesByCategory(
        getDeliverablesByCategory({
          contentfulData,
          personaPlans,
        })
      )
  }, [contentfulData])

  useEffect(() => {
    if (isEmpty(deliverablesByCategory)) return
    const personas = deliverablesByCategory.find(
      group => group.category === template.title
    )
    if (isEmpty(personas)) return
    setDeliverables(personas.deliverables)
  }, [deliverablesByCategory, template])

  const targetMarketIsComplete = useMemo(() => {
    return (
      templatesOverview?.find(template => template.id === '9')
        .templateStatus === TEMPLATE_STATUS.COMPLETE
    )
  }, [templatesOverview])

  const isBrandPositioning = useMemo(() => {
    return template.id === '10'
  }, [template])

  const isBrandPositioningSectionOne = useMemo(() => {
    return isBrandPositioning && template.currentStep === 0
  }, [template])

  const {
    currentStep,
    firstPercentGroupComplete,
    questions,
    questionIsMarketingRoleSection,
    questionIsPercentGroupSection,
    questionIsTargetMarket,
    questionIsMarketingResource,
    sections,
    questionIsMarketingGoals,
  } = template

  const [questionsInView, setQuestionsInView] = useState([])
  const [formValues, setFormValues] = useState({})
  const [backFromNextStep, setBackFromNextStep] = useState(false)
  const [nextButtonDisabled, setNextButtonDisabled] = useState(false)
  const [isMatrix, setIsMatrix] = useState(false)
  const [completedTargetMarket, setCompletedTargetMarket] = useState(false)
  const [marketingGoalsSubtitle, setMarketingGoalsSubtitle] = useState('')
  const { loading, data: targetMarketData } = useQuery(GET_QUESTIONS, {
    variables: { templateName: STRATEGYBUILDER_SECTION_NAME.TARGET_MARKET },
  })

  useEffect(() => {
    setCompletedTargetMarket(
      targetMarketData?.template?.templateStatus === 'Complete'
    )
  }, [loading, targetMarketData])

  useEffect(() => {
    if (completedTargetMarket)
      return setMarketingGoalsSubtitle(
        "You've identified these personas as members of your existing audience"
      )
    setMarketingGoalsSubtitle(
      <Fragment>
        go fill out{' '}
        <Link to="/app/StrategyBuilder/Section/Target Market">
          Target Market
        </Link>{' '}
        to see your personas
      </Fragment>
    )
  }, [completedTargetMarket])

  useEffect(() => {
    const currentQuestionGroup = questions[currentStep]
    if (questions.length && currentQuestionGroup?.length) {
      setTotalQuestionProgressSteps(1)
      setQuestionsInView(groupQuestions(currentQuestionGroup))
    }
  }, [questions, currentStep, questionProgressStep, firstPercentGroupComplete])

  useEffect(() => {
    // if coming back from the next step in a section and the new step has substeps, start on the last substep
    if (backFromNextStep && totalQuestionProgressSteps > 1) {
      setQuestionProgressStep(totalQuestionProgressSteps - 1)
    }
  }, [backFromNextStep])

  useEffect(() => {
    // current step changed, we update backFromNextStep in order to check if we need to start at the last substep of a question
    setBackFromNextStep(userHitBack)
  }, [currentStep])

  useEffect(() => {
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0
  }, [questionsInView])

  const onGoBack = async ({ values }) => {
    await saveProgress({ values })

    if (questionProgressStep > 0 && !questionIsMarketingRoleSection) {
      setQuestionProgressStep(questionProgressStep - 1)
    } else {
      if (currentStep === 0) {
        navigate('/app/StrategyBuilder')
      }
    }
  }

  const groupQuestions = questionsArr => {
    const grouped = groupBy(questionsArr, question =>
      question.questionType === null ? 'generic' : question.questionType
    )

    return Object.keys(grouped).map(key => ({
      type: key,
      questions: grouped[key],
    }))
  }

  const triggerSavingIndicator = () => {
    setSavingProgress(true)
    const saveTimer = setTimeout(() => {
      setSavingProgress(false)
    }, 3000)
    return () => clearTimeout(saveTimer)
  }

  const onGoNext = async ({ values }) => {
    await saveProgress({ values })
    triggerSavingIndicator()
    if (
      !questionIsPercentGroupSection &&
      (questionIsMarketingRoleSection ||
        totalQuestionProgressSteps === 0 ||
        questionProgressStep === totalQuestionProgressSteps - 1)
    ) {
      setQuestionProgressStep(0)
    } else {
      const newProgressStep = questionIsPercentGroupSection
        ? 0
        : questionProgressStep + 1
      setQuestionProgressStep(newProgressStep)
    }
  }

  const [submitAnswers, { loading: submitLoading }] = useMutation(
    SUBMIT_ANSWERS,
    {
      refetchQueries: [
        { query: GET_QUESTIONS, variables: { templateName: template?.title } },
        { query: GET_PERSONAS, variables: {} },
        {
          query: GET_TEMPLATE_OVERVIEW,
          variables: { templateCategory: TEMPLATE_CATEGORY.STRATEGY_BUILDER },
        },
      ],
      awaitRefetchQueries: true,
    }
  )

  const [saveProgressMutation, { loading: savingProgress }] = useMutation(
    SUBMIT_ANSWERS
  )

  const formatSavedAnswers = questionSection => {
    const savedAnswers = {}
    questionSection?.forEach(question => {
      const isPercentInput = question.answers[0]?.answerType === 'PercentInput'
      const selectedAnswers = question.answers?.filter(
        answer => answer.userSelectedAnswer
      )

      if (selectedAnswers.length) {
        if (selectedAnswers.length > 1 && !isPercentInput) {
          let hasRadioOrCheckbox = false
          let hasTextField = false

          selectedAnswers.forEach(answer => {
            if (
              answer.answerType === 'checkbox' ||
              answer.answerType === 'radio'
            ) {
              hasRadioOrCheckbox = true
            }
            if (
              answer.answerType === 'textarea' ||
              answer.answerType === 'text'
            ) {
              hasTextField = true
            }
          })

          if (hasRadioOrCheckbox && hasTextField) {
            // if we are here, we have a write in answer for a checkbox or radio
            if (selectedAnswers[0]?.answerType === 'checkbox') {
              // for checkboxes, we store the answer values in an array for a given question id
              savedAnswers[
                selectedAnswers[0]?.questionId
              ] = selectedAnswers
                .filter(answer => answer.answerType === 'checkbox')
                .map(answer => answer.id)
              const writeIn = selectedAnswers.find(
                answer =>
                  answer.answerType === 'text' ||
                  answer.answerType === 'textarea'
              )
              savedAnswers[writeIn.id] = writeIn.answerValue
            } else {
              savedAnswers[selectedAnswers[0]?.questionId] =
                selectedAnswers[0]?.id
              savedAnswers[selectedAnswers[1]?.id] =
                selectedAnswers[1]?.answerValue
            }
          } else {
            const answerIds = selectedAnswers.map(answer => answer.id)
            savedAnswers[selectedAnswers[0]?.questionId] = answerIds
          }
        } else {
          if (isPercentInput) {
            selectedAnswers.forEach(answer => {
              savedAnswers[answer.id] = answer.answerValue
            })
          } else {
            const answer = selectedAnswers[0]
            if (
              (answer.answerType !== 'text' &&
                answer.answerType !== 'textarea') ||
              answer.answerValue.length > 0
            ) {
              savedAnswers[answer.questionId] =
                answer.answerType === 'checkbox'
                  ? [answer.id]
                  : answer.answerValue || answer.id
            }
          }
        }
      }
    })
    return savedAnswers
  }
  const sectionOneIsComplete = questionAndAnswers => {
    if (
      currentStep === 1 &&
      questionProgressStep !== totalQuestionProgressSteps - 1
    ) {
      return false
    }
    // target markets question decides how many question steps are in section 1
    // if the user chooses 1 additional market, they get 3 steps. 2 additional markets, 4 steps.
    // a non-answer or choosing no additional target markets leads to just 2 steps and the section being marked as complete
    let selectedAnswer = null
    const targetMarketsQuestion = questions[1]?.[questions[1]?.length - 1]
    const answerGroup = questionAndAnswers.find(
      group => group.questionId === targetMarketsQuestion.id
    )
    // if we are updating the value of the targetMarketsQuestion in our submit, we need to reference new value.
    // Otherwise, use previously stored value
    if (answerGroup) {
      selectedAnswer = targetMarketsQuestion?.answers.find(
        answer => answer.id === answerGroup?.answers?.[0]?.answerId
      )
    } else {
      selectedAnswer = targetMarketsQuestion?.answers.find(
        answer => answer.userSelectedAnswer
      )
    }
    if (!selectedAnswer || selectedAnswer.order > 2) {
      return true
    }
    return (
      selectedAnswer.order + 1 === currentStep &&
      questionProgressStep === totalQuestionProgressSteps - 1
    )
  }

  const getAnswersObject = ({ values }) => {
    return Object.keys(values).length > 0 ? parseAnswers(values, template) : []
  }

  const getRequestObject = ({ values }) => {
    const answers = getAnswersObject({ values })
    const templateStatus = (() => {
      if (template.templateStatus === TEMPLATE_STATUS.COMPLETE)
        return TEMPLATE_STATUS.COMPLETE
      if (currentStep === template.questions.length - 1)
        return TEMPLATE_STATUS.COMPLETE
      return TEMPLATE_STATUS.IN_PROGRESS
    })()
    const request = {
      templateId: template.id,
      templateStatus: templateStatus,
      questionAndAnswers: answers,
    }
    return request
  }

  const saveProgress = async ({ values }) => {
    const request = getRequestObject({ values })
    await saveProgressMutation({ variables: { request } })
  }

  const createEmailLink = text => {
    const email = text.match(
      /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi
    )?.[0]
    if (!isEmpty(email)) {
      return text.replace(email, `<a href="mailto:${email}">${email}</a>`)
    }
    return text
  }

  useEffect(() => {
    setFormValues(formatSavedAnswers(questions[currentStep]))
  }, [questions, currentStep])

  useEffect(() => {
    generateFormikSchema({
      questions: questions[currentStep],
      type: 'strategy-builder',
    })
  }, [questions[currentStep]])

  useEffect(() => {
    setIsMatrix(sections?.[currentStep]?.id === '41')
  }, [rawTemplate, template, sections])

  const [questionHasTooManyAnswers, setQuestionHasTooManyAnswers] = useState(
    false
  )
  return (
    <Card
      variant="cards.question"
      sx={{
        width: 1060,
      }}
    >
      {submitLoading || savingProgress || answersUpdating ? (
        <Flex
          sx={{ justifyContent: 'center', minHeight: '300px', width: '100%' }}
        >
          <LoadingSpinner sx={{ mt: 2 }} />
        </Flex>
      ) : (
        <Flex sx={{ flexDirection: 'column', width: '100%' }}>
          <Formik
            enableReinitialize
            initialValues={formValues}
            validationSchema={formikSchema}
            onSubmit={async values => {
              cleanUpIsHide(values)
              if (questionIsPercentGroupSection) {
                if (percentDesiredError || percentCurrentError) {
                  return
                }
              }
              const answers = getAnswersObject({ values })
              const request = getRequestObject({ values })
              if (!questionIsPercentGroupSection || firstPercentGroupComplete) {
                if (userHitBack) {
                  request.currentQuestionNumber =
                    questionProgressStep === 0 || totalQuestionProgressSteps < 2
                      ? Math.max(currentStep, 1)
                      : currentStep + 1
                } else {
                  const increment =
                    totalQuestionProgressSteps < 2 ||
                    questionProgressStep === totalQuestionProgressSteps - 1 ||
                    questionIsMarketingRoleSection
                      ? 2
                      : 1
                  const currentQuestionNumber = currentStep + increment
                  request.currentQuestionNumber =
                    currentQuestionNumber > sections.length
                      ? sections.length
                      : currentQuestionNumber
                }
                if (
                  template.templateStatus === TEMPLATE_STATUS.COMPLETE ||
                  (!userHitBack &&
                    ((template.currentStep === sections.length - 1 &&
                      questionProgressStep ===
                        totalQuestionProgressSteps - 1) ||
                      (template.title ===
                        STRATEGYBUILDER_SECTION_NAME.BUSINESS_MIX &&
                        currentStep > 0 &&
                        sectionOneIsComplete(answers))))
                ) {
                  request.templateStatus = TEMPLATE_STATUS.COMPLETE
                }
              }
              await submitAnswers({ variables: { request } })
              // if we are submitting from the last question and hitting next, update step number to go to success message
              if (
                !userHitBack &&
                currentStep === sections.length - 1 &&
                request.templateStatus === TEMPLATE_STATUS.COMPLETE &&
                questionProgressStep === totalQuestionProgressSteps - 1
              ) {
                updateTemplate({ currentStep: currentStep + 1 })
              }
            }}
          >
            {({ values }) => (
              <Fragment>
                <Flex
                  sx={{
                    flexDirection: 'column',
                    mx: [3, 32],
                    mt: 5,
                  }}
                >
                  {sections[currentStep]?.title && (
                    <Text
                      data-automation-id={`title-question-card-${sections[currentStep]?.title}`}
                      variant="h5"
                      sx={{ fontWeight: 'normal' }}
                    >
                      {sections[currentStep]?.title}
                    </Text>
                  )}
                  {sections[currentStep]?.subTitle &&
                    (!isBrandPositioningSectionOne ? (
                      <Fragment>
                        {!questionIsMarketingGoals ? (
                          <Text
                            dangerouslySetInnerHTML={{
                              __html: createEmailLink(
                                sections[currentStep]?.subTitle
                              ),
                            }}
                            variant="mediumText"
                            sx={{
                              color: 'black',
                              my: 4,
                              mb: questionIsTargetMarket && 0,
                              '> a': {
                                color: 'forest',
                                textTransform: 'lowercase',
                              },
                            }}
                          />
                        ) : (
                          <Text
                            variant="mediumText"
                            sx={{ color: 'black', my: 4 }}
                          >
                            {marketingGoalsSubtitle}
                          </Text>
                        )}
                      </Fragment>
                    ) : targetMarketIsComplete ? (
                      <div>
                        <Text
                          variant="mediumText"
                          sx={{ color: 'black', my: 4 }}
                        >
                          {sections[currentStep]?.subTitle}
                        </Text>
                        <Flex sx={{ flexDirection: 'row', flex: 1 }}>
                          {deliverables.map(
                            (
                              {
                                descriptionBrandPositioning,
                                description,
                                ...rest
                              },
                              index
                            ) => (
                              <Persona
                                index={index}
                                isForQuestionCard
                                key={index}
                                description={
                                  isBrandPositioning
                                    ? descriptionBrandPositioning
                                    : description
                                }
                                {...rest}
                              />
                            )
                          )}
                        </Flex>
                      </div>
                    ) : (
                      <Text variant="mediumText" sx={{ color: 'black', my: 4 }}>
                        Go fill out{' '}
                        <Link
                          sx={{ textDecoration: 'none' }}
                          href="/app/StrategyBuilder/Section/Target%20Market"
                        >
                          Target Market{' '}
                        </Link>
                        to see your personas
                      </Text>
                    ))}
                </Flex>
                <Form sx={{ width: '100%' }}>
                  <Flex
                    sx={{
                      flexDirection: 'column',
                      '> section:last-of-type': {
                        mb:
                          (questionIsTargetMarket ||
                            questionIsMarketingResource) &&
                          '60px',
                      },
                    }}
                  >
                    {isMatrix ? (
                      <Matrix />
                    ) : (
                      questionsInView.map(group => (
                        <QuestionType
                          key={group.type}
                          setQuestionHasTooManyAnswers={
                            setQuestionHasTooManyAnswers
                          }
                          questionHasTooManyAnswers={questionHasTooManyAnswers}
                          {...group}
                          isStrategyBuilderQuestion
                        />
                      ))
                    )}
                    <Flex sx={{ ml: 6 }}>
                      <QuestionButtonRow
                        questionHasTooManyAnswers={questionHasTooManyAnswers}
                        nextButtonDisabled={
                          nextButtonDisabled ||
                          percentDesiredError ||
                          percentCurrentError
                        }
                        setNextButtonDisabled={setNextButtonDisabled}
                        questionsInView={questionsInView}
                        handleContinue={() => onGoNext({ values })}
                        handleBack={() => onGoBack({ values })}
                        isStrategyBuilderQuestion
                      />
                    </Flex>
                  </Flex>
                </Form>
              </Fragment>
            )}
          </Formik>
          {!questionIsPercentGroupSection &&
            !questionIsMarketingRoleSection &&
            totalQuestionProgressSteps > 1 && (
              <Flex sx={{ justifyContent: 'center', width: '100%' }}>
                <ProgressBar
                  currentStep={questionProgressStep}
                  totalStepNum={totalQuestionProgressSteps}
                />
              </Flex>
            )}
        </Flex>
      )}
    </Card>
  )
}

QuestionCard.propTypes = {
  templatesOverview: any,
}

export default QuestionCard
